import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { faFileLines, faImage, faNewspaper, faPlay } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";
import { LinkedinSignalMedia } from "@/pages/Private/pages/ProfileTracker/schema/trackerSignalResult";

interface LinkedinSignalPostMediaProps {
	media: LinkedinSignalMedia[];
	/** Link-out target when a media tile is clicked (the original post). */
	postUrl?: string | null;
}

/** LinkedIn caps a photo post at four visible tiles; extras collapse into a "+N" overlay. */
const MAX_VISIBLE_IMAGES = 4;

const MediaImage: FunctionComponent<{ url: string; alt: string; className?: string }> = ({
	url,
	alt,
	className,
}) => {
	const [hasError, setHasError] = useState(false);

	if (hasError) {
		return (
			<div
				className={classNames(
					"flex items-center justify-center bg-background-light text-text-meta",
					className
				)}
			>
				<Icon className="h-5 w-5" icon={faImage} />
			</div>
		);
	}

	return (
		<img
			alt={alt}
			className={classNames("object-cover", className)}
			loading="lazy"
			src={url}
			onError={() => setHasError(true)}
		/>
	);
};

/** Grid template per image count, mirroring LinkedIn's collage layout. */
const gridClassForCount = (count: number): string => (count === 1 ? "grid-cols-1" : "grid-cols-2");

const tileClassForImage = (count: number, index: number): string => {
	if (count === 1) {
		return "max-h-[420px] min-h-[180px] w-full";
	}

	// 3 images: first spans the full width on top, the other two share the row below.
	if (count === 3 && index === 0) {
		return "col-span-2 aspect-[2/1] w-full";
	}

	return "aspect-square w-full";
};

const ImageGallery: FunctionComponent<{
	images: LinkedinSignalMedia[];
	postUrl?: string | null;
}> = ({ images, postUrl }) => {
	const { t } = useTranslation();
	const visible = images.slice(0, MAX_VISIBLE_IMAGES);
	const remaining = images.length - visible.length;

	const tile = (image: LinkedinSignalMedia, index: number) => {
		const isOverlay = remaining > 0 && index === visible.length - 1;
		const content = (
			<div className="relative h-full w-full overflow-hidden">
				<MediaImage
					alt={t("profileTracker.signalTimeline.postImage")}
					className="h-full w-full"
					url={image.url}
				/>
				{isOverlay && (
					<div className="absolute inset-0 flex items-center justify-center bg-black/50 text-lg font-semibold text-white">
						+{remaining + 1}
					</div>
				)}
			</div>
		);

		const tileClass = classNames(
			"overflow-hidden bg-background-light",
			tileClassForImage(visible.length, index)
		);

		return postUrl ? (
			<a
				key={image.url}
				className={classNames(tileClass, "block cursor-pointer")}
				href={postUrl}
				rel="noreferrer"
				target="_blank"
				onClick={(event) => event.stopPropagation()}
			>
				{content}
			</a>
		) : (
			<div key={image.url} className={tileClass}>
				{content}
			</div>
		);
	};

	return (
		<div
			className={classNames(
				"mt-3 grid gap-0.5 overflow-hidden rounded-lg border border-border-light",
				gridClassForCount(visible.length)
			)}
			data-testid="linkedin-post-image-gallery"
		>
			{visible.map(tile)}
		</div>
	);
};

const VideoTile: FunctionComponent<{ media: LinkedinSignalMedia; postUrl?: string | null }> = ({
	media,
	postUrl,
}) => {
	const { t } = useTranslation();
	const href = postUrl ?? media.url;

	return (
		<a
			className="relative mt-3 block aspect-video cursor-pointer overflow-hidden rounded-lg border border-border-light bg-background-light"
			data-testid="linkedin-post-video"
			href={href}
			rel="noreferrer"
			target="_blank"
			onClick={(event) => event.stopPropagation()}
		>
			{media.thumbnailUrl && (
				<MediaImage
					alt={t("profileTracker.signalTimeline.playVideo")}
					className="h-full w-full"
					url={media.thumbnailUrl}
				/>
			)}
			<div className="absolute inset-0 flex items-center justify-center">
				<span className="flex h-12 w-12 items-center justify-center rounded-full bg-black/60 text-white">
					<Icon className="h-5 w-5" icon={faPlay} />
				</span>
			</div>
		</a>
	);
};

const LinkTile: FunctionComponent<{ media: LinkedinSignalMedia; postUrl?: string | null }> = ({
	media,
	postUrl,
}) => {
	const { t } = useTranslation();
	const href = media.url || postUrl || undefined;
	const icon = media.type === "article" ? faNewspaper : faFileLines;
	const label = media.title || t("profileTracker.signalTimeline.viewDocument");

	return (
		<a
			className="mt-3 flex cursor-pointer items-center gap-3 rounded-lg border border-border-light bg-white p-3 hover:bg-background-light"
			data-testid={`linkedin-post-${media.type}`}
			href={href}
			rel="noreferrer"
			target="_blank"
			onClick={(event) => event.stopPropagation()}
		>
			{media.thumbnailUrl ? (
				<MediaImage alt={label} className="h-12 w-12 shrink-0 rounded" url={media.thumbnailUrl} />
			) : (
				<span className="flex h-12 w-12 shrink-0 items-center justify-center rounded bg-background-light text-text-meta">
					<Icon className="h-5 w-5" icon={icon} />
				</span>
			)}
			<div className="min-w-0 flex-1">
				<div className="truncate text-[13px] font-semibold text-text">{label}</div>
				{media.subtitle && <div className="truncate text-xs text-text-meta">{media.subtitle}</div>}
			</div>
		</a>
	);
};

/**
 * Renders a LinkedIn post's attachments: images as a collage gallery (1 full,
 * 2 side-by-side, 3 top+pair, 4 grid, 5+ with a "+N" overlay), videos as a
 * thumbnail with a play badge, and documents/articles as link cards. Each tile
 * links out to the original post.
 */
export const LinkedinSignalPostMedia: FunctionComponent<LinkedinSignalPostMediaProps> = ({
	media,
	postUrl,
}) => {
	if (!media.length) {
		return null;
	}

	const images = media.filter((item) => item.type === "image");
	const videos = media.filter((item) => item.type === "video");
	const links = media.filter((item) => item.type === "document" || item.type === "article");

	return (
		<div data-testid="linkedin-post-media">
			{images.length > 0 && <ImageGallery images={images} postUrl={postUrl} />}
			{videos.map((video) => (
				<VideoTile key={video.url} media={video} postUrl={postUrl} />
			))}
			{links.map((link) => (
				<LinkTile key={link.url} media={link} postUrl={postUrl} />
			))}
		</div>
	);
};
